import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  ArrowLeft,
  Check,
  CheckCircle2,
  HardDrive,
  Loader2,
  Lock,
  ShieldCheck,
} from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/sonner';
import { supabase } from '@/lib/supabase';
import { loadStripe } from '@/lib/stripe';
import { useAuth } from '@/contexts/AuthContext';

import {
  KINSSPACE_PACKS,
  type BillingCycle,
  type KinsSpacePackId,
} from '@/lib/business/KinsCircleBusinessEngine';

interface KinsSpacePlansModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  hasPaidMembership: boolean;
  onMembershipRequired: () => void;
}

interface CatalogPreflightResult {
  status?: string;
  packCount?: number;
  priceCount?: number;
  externalChargeAttempted?: boolean;
  membershipSubscriptionModified?: boolean;
}

interface PurchaseReceipt {
  subscriptionId: string;
  status: string;
  recovered: boolean;
  persistenceVerified: boolean;
  invoiceId: string | null;
  hostedInvoiceUrl: string | null;
  invoicePdfUrl: string | null;
  receiptUrl: string | null;
}

type Step =
  | 'plans'
  | 'payment'
  | 'success';

const PACK_ORDER: KinsSpacePackId[] = [
  'kinsspace_100',
  'kinsspace_250',
  'kinsspace_500',
  'kinsspace_1tb',
  'kinsspace_2tb',
];

const formatStorage = (
  storageGB: number,
): string =>
  storageGB >= 1000
    ? `${storageGB / 1000} TB`
    : `${storageGB} GB`;

const formatPrice = (
  amount: number,
): string =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount);

const friendlyStripeError = (
  error: any,
): string => {
  const code =
    error?.decline_code ||
    error?.code ||
    '';

  const messages: Record<string, string> = {
    card_declined:
      'Your card was declined. Please try another card.',
    insufficient_funds:
      'This card has insufficient funds.',
    expired_card:
      'This card has expired.',
    incorrect_cvc:
      'The card security code is incorrect.',
    incorrect_number:
      'The card number is incorrect.',
    processing_error:
      'The card issuer reported a temporary processing error. Please try again.',
    authentication_required:
      'Your bank requires additional authentication.',
    setup_intent_authentication_failure:
      'Card authentication failed. Please try again.',
    payment_intent_authentication_failure:
      'Payment authentication failed. Please try again.',
  };

  return (
    messages[code] ||
    error?.message ||
    'Payment could not be completed.'
  );
};

const KinsSpacePlansModal: React.FC<
  KinsSpacePlansModalProps
> = ({
  open,
  onOpenChange,
  hasPaidMembership,
  onMembershipRequired,
}) => {
  const { user, profile } = useAuth();

  const [step, setStep] =
    useState<Step>('plans');

  const [cycle, setCycle] =
    useState<BillingCycle>('monthly');

  const [
    selectedPackId,
    setSelectedPackId,
  ] = useState<KinsSpacePackId>(
    'kinsspace_100',
  );

  const [
    checkingCatalog,
    setCheckingCatalog,
  ] = useState(false);

  const [
    catalogVerified,
    setCatalogVerified,
  ] = useState(false);

  const [loading, setLoading] =
    useState(false);

  const [
    paymentReady,
    setPaymentReady,
  ] = useState(false);

  const [error, setError] =
    useState<string | null>(null);

  const [
    clientSecret,
    setClientSecret,
  ] = useState<string | null>(null);

  const [customerId, setCustomerId] =
    useState<string | null>(null);

  const [
    activeSubscriptionId,
    setActiveSubscriptionId,
  ] = useState<string | null>(null);

  const [
    purchaseAttemptId,
    setPurchaseAttemptId,
  ] = useState<string | null>(null);

  const [
    receipt,
    setReceipt,
  ] = useState<PurchaseReceipt | null>(null);

  const stripeRef = useRef<any>(null);
  const elementsRef = useRef<any>(null);
  const paymentElementRef =
    useRef<any>(null);

  const paymentMountRef =
    useRef<HTMLDivElement>(null);

  const selectedPack =
    KINSSPACE_PACKS[selectedPackId];

  const selectedPrice = useMemo(
    () =>
      cycle === 'yearly'
        ? selectedPack.yearlyPrice
        : selectedPack.monthlyPrice,
    [cycle, selectedPack],
  );

  useEffect(() => {
    if (!open) return;

    setStep('plans');
    setError(null);
    setPaymentReady(false);
    setClientSecret(null);
    setCustomerId(null);
    setActiveSubscriptionId(null);
    setPurchaseAttemptId(null);
    setReceipt(null);

    let cancelled = false;

    const verifyCatalog = async () => {
      setCheckingCatalog(true);
      setCatalogVerified(false);

      try {
        const { data, error } =
          await supabase.functions.invoke(
            'kinsvault-subscription',
            {
              body: {
                action:
                  'preflight-kinsspace-catalog',
              },
            },
          );

        if (error) throw error;

        const result =
          data as CatalogPreflightResult;

        const passed =
          result?.status === 'PASS' &&
          result?.packCount === 5 &&
          result?.priceCount === 10 &&
          result?.externalChargeAttempted ===
            false &&
          result
            ?.membershipSubscriptionModified ===
            false;

        if (!passed) {
          throw new Error(
            'KinsSpace catalog verification failed.',
          );
        }

        if (!cancelled) {
          setCatalogVerified(true);
        }
      } catch (catalogError) {
        console.error(
          '[KinsSpace] catalog verification failed',
          catalogError,
        );

        if (!cancelled) {
          setCatalogVerified(false);
          setError(
            'KinsSpace plans could not be verified.',
          );
        }
      } finally {
        if (!cancelled) {
          setCheckingCatalog(false);
        }
      }
    };

    void verifyCatalog();

    return () => {
      cancelled = true;
    };
  }, [open]);

  const startPurchaseAttempt =
    async (
      values: {
        customerId: string | null;
        setupIntentId: string | null;
        recovered?: boolean;
      },
    ) => {
      if (!user?.id) {
        throw new Error(
          'AUTHENTICATION_REQUIRED',
        );
      }

      const {
        data,
        error: attemptError,
      } = await supabase.rpc(
        'start_billing_purchase_attempt',
        {
          p_product_type:
            'kinsspace',
          p_product_reference:
            selectedPackId,
          p_billing_cycle:
            cycle,
          p_amount_cents:
            Math.round(
              selectedPrice * 100,
            ),
          p_currency: 'usd',
          p_stripe_customer_id:
            values.customerId,
          p_stripe_setup_intent_id:
            values.setupIntentId,
          p_metadata: {
            storageGB:
              selectedPack.storageGB,
            recovered:
              values.recovered === true,
          },
        },
      );

      if (attemptError) {
        throw attemptError;
      }

      const attemptId =
        typeof data?.id === 'string'
          ? data.id
          : null;

      if (!attemptId) {
        throw new Error(
          'Purchase attempt could not be recorded.',
        );
      }

      setPurchaseAttemptId(
        attemptId,
      );

      return attemptId;
    };

  const transitionPurchaseAttempt =
    async (
      attemptId: string | null,
      values: {
        stage: string;
        status:
          | 'started'
          | 'pending'
          | 'requires_action'
          | 'succeeded'
          | 'failed'
          | 'recovered'
          | 'cancelled';
        paymentMethodId?: string | null;
        paymentIntentId?: string | null;
        subscriptionId?: string | null;
        invoiceId?: string | null;
        hostedInvoiceUrl?: string | null;
        invoicePdfUrl?: string | null;
        receiptUrl?: string | null;
        failureCode?: string | null;
        failureReason?: string | null;
        retryable?: boolean | null;
        metadata?: Record<string, unknown>;
      },
    ) => {
      if (!attemptId) return;

      const {
        error: transitionError,
      } = await supabase.rpc(
        'transition_billing_purchase_attempt',
        {
          p_attempt_id:
            attemptId,
          p_stage:
            values.stage,
          p_status:
            values.status,
          p_stripe_payment_method_id:
            values.paymentMethodId ??
            null,
          p_stripe_payment_intent_id:
            values.paymentIntentId ??
            null,
          p_stripe_subscription_id:
            values.subscriptionId ??
            null,
          p_stripe_invoice_id:
            values.invoiceId ??
            null,
          p_hosted_invoice_url:
            values.hostedInvoiceUrl ??
            null,
          p_invoice_pdf_url:
            values.invoicePdfUrl ??
            null,
          p_receipt_url:
            values.receiptUrl ??
            null,
          p_failure_code:
            values.failureCode ??
            null,
          p_failure_reason:
            values.failureReason ??
            null,
          p_retryable:
            values.retryable ??
            null,
          p_metadata_patch:
            values.metadata ?? {},
        },
      );

      if (transitionError) {
        console.error(
          '[KinsSpace] purchase attempt transition failed',
          transitionError,
        );
      }
    };

  const stripeFailureCode = (
    paymentError: any,
  ): string =>
    String(
      paymentError?.decline_code ||
      paymentError?.code ||
      paymentError?.gateway_step ||
      'payment_failed',
    );

  const isRetryablePaymentError = (
    code: string,
  ): boolean =>
    ![
      'lost_card',
      'stolen_card',
      'fraudulent',
      'do_not_honor',
      'pickup_card',
    ].includes(code);

  const showVerifiedSuccess = (
    values: {
      subscriptionId: string;
      status: string;
      recovered?: boolean;
      persistenceVerified: boolean;
      invoiceId?: string | null;
      hostedInvoiceUrl?: string | null;
      invoicePdfUrl?: string | null;
      receiptUrl?: string | null;
    },
  ) => {
    if (
      !values.persistenceVerified ||
      (
        values.status !== 'active' &&
        values.status !== 'trialing'
      )
    ) {
      throw new Error(
        `KinsSpace is not fully verified. Status: ${values.status}`,
      );
    }

    setActiveSubscriptionId(
      values.subscriptionId,
    );

    setReceipt({
      subscriptionId:
        values.subscriptionId,
      status:
        values.status,
      recovered:
        values.recovered === true,
      persistenceVerified:
        values.persistenceVerified,
      invoiceId:
        values.invoiceId ?? null,
      hostedInvoiceUrl:
        values.hostedInvoiceUrl ??
        null,
      invoicePdfUrl:
        values.invoicePdfUrl ??
        null,
      receiptUrl:
        values.receiptUrl ?? null,
    });

    setStep('success');
  };

  useEffect(() => {
    if (
      step !== 'payment' ||
      !clientSecret ||
      !paymentMountRef.current
    ) {
      return;
    }

    let cancelled = false;

    setPaymentReady(false);

    const mountPaymentElement =
      async () => {
        try {
          const stripe =
            stripeRef.current ||
            await loadStripe();

          if (!stripe || cancelled) {
            throw new Error(
              'Stripe could not be loaded.',
            );
          }

          stripeRef.current = stripe;

          const elements =
            stripe.elements({
              clientSecret,
              appearance: {
                theme: 'stripe',
                variables: {
                  colorPrimary: '#b45309',
                  colorBackground:
                    '#ffffff',
                  colorText: '#1e293b',
                  borderRadius: '12px',
                },
              },
            });

          elementsRef.current = elements;

          const paymentElement =
            elements.create(
              'payment',
              {
                layout: 'tabs',
              },
            );

          paymentElement.on(
            'ready',
            () => {
              if (cancelled) return;

              setPaymentReady(true);
              setError(null);
            },
          );

          paymentElement.on(
            'loaderror',
            (event: any) => {
              if (cancelled) return;

              setPaymentReady(false);
              setError(
                event?.error?.message ||
                  'The secure payment form could not load.',
              );
            },
          );

          paymentElement.mount(
            paymentMountRef.current,
          );

          paymentElementRef.current =
            paymentElement;
        } catch (mountError: any) {
          console.error(
            '[KinsSpace] Stripe mount failed',
            mountError,
          );

          if (!cancelled) {
            setPaymentReady(false);
            setError(
              mountError?.message ||
                'The payment form could not load.',
            );
          }
        }
      };

    void mountPaymentElement();

    return () => {
      cancelled = true;

      try {
        paymentElementRef.current
          ?.unmount();
      } catch {
        // no-op
      }

      paymentElementRef.current = null;
      elementsRef.current = null;
      setPaymentReady(false);
    };
  }, [clientSecret, step]);

  const beginCheckout = async () => {
    if (!hasPaidMembership) {
      onOpenChange(false);
      onMembershipRequired();
      return;
    }

    if (!user?.id) {
      setError(
        'Please sign in before purchasing KinsSpace.',
      );
      return;
    }

    if (!catalogVerified) {
      setError(
        'KinsSpace checkout is locked until the catalog passes verification.',
      );
      return;
    }

    setLoading(true);
    setError(null);
    setPaymentReady(false);
    setReceipt(null);

    try {
      const recovery =
        await supabase.functions.invoke(
          'kinsvault-subscription',
          {
            body: {
              action:
                'recover-kinsspace-subscription',
              userId: user.id,
            },
          },
        );

      if (recovery.error) {
        throw new Error(
          recovery.error.message,
        );
      }

      if (recovery.data?.error) {
        throw new Error(
          recovery.data.error,
        );
      }

      if (recovery.data?.found) {
        const recoveredAttemptId =
          await startPurchaseAttempt({
            customerId:
              recovery.data.customerId ??
              null,
            setupIntentId: null,
            recovered: true,
          });

        const recoveryVerification =
          await supabase.functions.invoke(
            'kinsvault-subscription',
            {
              body: {
                action:
                  'verify-kinsspace-subscription',
                subscriptionId:
                  recovery.data.subscriptionId,
                userId:
                  user.id,
                attemptId:
                  recoveredAttemptId,
                recovered: true,
              },
            },
          );

        if (recoveryVerification.error) {
          throw new Error(
            recoveryVerification.error.message,
          );
        }

        if (recoveryVerification.data?.error) {
          throw new Error(
            recoveryVerification.data.error,
          );
        }

        if (
          recoveryVerification.data
            ?.billingAttemptFinalized !== true
        ) {
          throw new Error(
            'Recovered KinsSpace billing evidence was not server-finalized.',
          );
        }

        showVerifiedSuccess({
          subscriptionId:
            recoveryVerification.data
              .subscriptionId,
          status:
            String(
              recoveryVerification.data
                .status || 'unknown',
            ),
          recovered: true,
          persistenceVerified:
            recoveryVerification.data
              .persistenceVerified ===
            true,
          invoiceId:
            recoveryVerification.data
              .invoiceId,
          hostedInvoiceUrl:
            recoveryVerification.data
              .hostedInvoiceUrl,
          invoicePdfUrl:
            recoveryVerification.data
              .invoicePdfUrl,
          receiptUrl:
            recoveryVerification.data
              .receiptUrl,
        });

        toast.success(
          'Your existing KinsSpace subscription was recovered.',
        );

        return;
      }

      const { data, error } =
        await supabase.functions.invoke(
          'kinsvault-subscription',
          {
            body: {
              action:
                'create-kinsspace-setup-intent',
              packId:
                selectedPackId,
              billingCycle:
                cycle,
              userId:
                user.id,
              name:
                profile?.name || '',
            },
          },
        );

      if (error) {
        throw new Error(
          error.message,
        );
      }

      if (data?.error) {
        throw new Error(
          data.error,
        );
      }

      if (
        !data?.clientSecret ||
        !data?.customerId ||
        !data?.setupIntentId
      ) {
        throw new Error(
          'KinsSpace checkout could not be started.',
        );
      }

      if (
        data
          ?.membershipSubscriptionModified !==
          false ||
        data?.externalChargeAttempted !==
          false
      ) {
        throw new Error(
          'KinsSpace checkout safety verification failed.',
        );
      }

      const attemptId =
        await startPurchaseAttempt({
          customerId:
            data.customerId,
          setupIntentId:
            data.setupIntentId,
        });

      setPurchaseAttemptId(
        attemptId,
      );

      setClientSecret(
        data.clientSecret,
      );

      setCustomerId(
        data.customerId,
      );

      setStep('payment');
    } catch (checkoutError: any) {
      console.error(
        '[KinsSpace] setup or recovery failed',
        checkoutError,
      );

      setError(
        friendlyStripeError(
          checkoutError,
        ),
      );
    } finally {
      setLoading(false);
    }
  };

  const activateSubscription =
    async () => {
      if (
        !paymentReady ||
        !stripeRef.current ||
        !elementsRef.current ||
        !customerId ||
        !user?.id ||
        !purchaseAttemptId
      ) {
        setError(
          'The secure payment form is not ready.',
        );
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const submitResult =
          await elementsRef.current.submit();

        if (submitResult?.error) {
          throw submitResult.error;
        }

        const confirmation =
          await stripeRef.current
            .confirmSetup({
              elements:
                elementsRef.current,
              confirmParams: {
                return_url:
                  window.location.origin,
              },
              redirect:
                'if_required',
            });

        if (confirmation?.error) {
          throw confirmation.error;
        }

        const setupIntent =
          confirmation?.setupIntent;

        if (
          setupIntent?.status !==
            'succeeded' ||
          !setupIntent
            ?.payment_method
        ) {
          throw new Error(
            `Card setup did not complete. Status: ${
              setupIntent?.status ||
              'unknown'
            }`,
          );
        }

        const paymentMethodId =
          String(
            setupIntent.payment_method,
          );

        await transitionPurchaseAttempt(
          purchaseAttemptId,
          {
            stage:
              'payment_method_confirmed',
            status: 'pending',
            paymentMethodId,
          },
        );

        const { data, error } =
          await supabase.functions.invoke(
            'kinsvault-subscription',
            {
              body: {
                action:
                  'activate-kinsspace-subscription',
                customerId,
                paymentMethodId,
                packId:
                  selectedPackId,
                billingCycle:
                  cycle,
                userId:
                  user.id,
                attemptId:
                  purchaseAttemptId,
              },
            },
          );

        if (error) {
          throw new Error(
            error.message,
          );
        }

        if (data?.error) {
          const gatewayError: any =
            new Error(
              data.error,
            );

          gatewayError.gateway_step =
            data.gateway_step;

          gatewayError.code =
            data.gateway_body
              ?.error?.code ??
            data.gateway_body
              ?.error?.decline_code ??
            data.gateway_step;

          throw gatewayError;
        }

        if (
          data
            ?.membershipSubscriptionModified !==
          false
        ) {
          throw new Error(
            'Membership isolation verification failed.',
          );
        }

        let finalStatus =
          String(
            data?.status || '',
          );

        const subscriptionId =
          typeof data
            ?.subscriptionId ===
            'string'
            ? data.subscriptionId
            : null;

        if (!subscriptionId) {
          throw new Error(
            'Stripe did not return a subscription reference.',
          );
        }

        if (
          data?.requiresAction &&
          data
            ?.pendingPaymentIntentClientSecret
        ) {
          await transitionPurchaseAttempt(
            purchaseAttemptId,
            {
              stage:
                'payment_authentication_required',
              status:
                'requires_action',
              paymentMethodId,
              metadata: {
                serverRequiresAction: true,
              },
            },
          );

          const paymentConfirmation =
            await stripeRef.current
              .confirmCardPayment(
                data
                  .pendingPaymentIntentClientSecret,
              );

          if (
            paymentConfirmation
              ?.error
          ) {
            throw paymentConfirmation
              .error;
          }
        }

        const verification =
          await supabase.functions.invoke(
            'kinsvault-subscription',
            {
              body: {
                action:
                  'verify-kinsspace-subscription',
                subscriptionId,
                userId:
                  user.id,
                attemptId:
                  purchaseAttemptId,
                recovered:
                  data?.recovered === true,
              },
            },
          );

        if (verification.error) {
          throw new Error(
            verification.error.message,
          );
        }

        if (
          verification.data?.error
        ) {
          throw new Error(
            verification.data.error,
          );
        }

        finalStatus =
          String(
            verification.data
              ?.status || '',
          );

        const persistenceVerified =
          verification.data
            ?.persistenceVerified ===
          true;

        if (
          (
            finalStatus !== 'active' &&
            finalStatus !== 'trialing'
          ) ||
          !persistenceVerified ||
          verification.data
            ?.billingAttemptFinalized !== true
        ) {
          throw new Error(
            `Stripe and KinsCircle did not fully agree. Status: ${finalStatus}`,
          );
        }

        const finalReceipt = {
          subscriptionId,
          status:
            finalStatus,
          recovered:
            data?.recovered === true,
          persistenceVerified,
          invoiceId:
            verification.data
              ?.invoiceId ??
            data?.invoiceId ??
            null,
          hostedInvoiceUrl:
            verification.data
              ?.hostedInvoiceUrl ??
            data?.hostedInvoiceUrl ??
            null,
          invoicePdfUrl:
            verification.data
              ?.invoicePdfUrl ??
            data?.invoicePdfUrl ??
            null,
          receiptUrl:
            verification.data
              ?.receiptUrl ??
            data?.receiptUrl ??
            null,
        };

        showVerifiedSuccess(
          finalReceipt,
        );

        toast.success(
          'KinsSpace is active.',
          {
            description:
              `${formatStorage(
                selectedPack.storageGB,
              )} of additional family storage was added.`,
          },
        );
      } catch (paymentError: any) {
        console.error(
          '[KinsSpace] activation failed',
          paymentError,
        );

        const failureCode =
          stripeFailureCode(
            paymentError,
          );

        const safeMessage =
          friendlyStripeError(
            paymentError,
          );

        await transitionPurchaseAttempt(
          purchaseAttemptId,
          {
            stage:
              paymentError
                ?.gateway_step ||
              'payment_activation_failed',
            status: 'failed',
            failureCode,
            failureReason:
              safeMessage,
            retryable:
              isRetryablePaymentError(
                failureCode,
              ),
          },
        );

        setError(
          safeMessage,
        );
      } finally {
        setLoading(false);
      }
    };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
    >
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-3xl">
        <DialogHeader>
          <div className="mb-2 flex items-center gap-2 text-amber-700">
            <HardDrive className="h-5 w-5" />

            <span className="text-xs font-extrabold uppercase tracking-widest">
              KinsSpace
            </span>
          </div>

          <DialogTitle className="font-serif text-2xl">
            {step === 'success'
              ? 'Your KinsSpace is active'
              : 'Add more room for family memories'}
          </DialogTitle>

          <DialogDescription>
            KinsSpace is billed separately
            and never changes your KinsCircle
            membership subscription.
          </DialogDescription>
        </DialogHeader>

        {step === 'plans' && (
          <>
            <div className="mt-2 rounded-2xl border border-amber-200 bg-amber-50 p-3">
              <div className="flex items-center gap-2 text-sm font-semibold text-amber-950">
                {checkingCatalog ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : catalogVerified ? (
                  <ShieldCheck className="h-4 w-4" />
                ) : (
                  <HardDrive className="h-4 w-4" />
                )}

                {checkingCatalog
                  ? 'Verifying live Stripe catalog…'
                  : catalogVerified
                    ? 'Five packs and ten Stripe prices verified'
                    : 'Catalog verification is required'}
              </div>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-2 rounded-2xl bg-slate-100 p-1">
              <Button
                type="button"
                variant={
                  cycle === 'monthly'
                    ? 'default'
                    : 'ghost'
                }
                onClick={() =>
                  setCycle('monthly')
                }
                className={
                  cycle === 'monthly'
                    ? 'bg-amber-700 text-white hover:bg-amber-800'
                    : ''
                }
              >
                Monthly
              </Button>

              <Button
                type="button"
                variant={
                  cycle === 'yearly'
                    ? 'default'
                    : 'ghost'
                }
                onClick={() =>
                  setCycle('yearly')
                }
                className={
                  cycle === 'yearly'
                    ? 'bg-amber-700 text-white hover:bg-amber-800'
                    : ''
                }
              >
                Yearly
              </Button>
            </div>

            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              {PACK_ORDER.map(
                (packId) => {
                  const pack =
                    KINSSPACE_PACKS[
                      packId
                    ];

                  const selected =
                    selectedPackId ===
                    packId;

                  const price =
                    cycle ===
                    'yearly'
                      ? pack.yearlyPrice
                      : pack.monthlyPrice;

                  return (
                    <button
                      key={pack.id}
                      type="button"
                      onClick={() =>
                        setSelectedPackId(
                          pack.id,
                        )
                      }
                      className={`rounded-2xl border p-4 text-left transition ${
                        selected
                          ? 'border-amber-500 bg-amber-50 ring-2 ring-amber-200'
                          : 'border-slate-200 bg-white hover:border-amber-300'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="font-serif text-lg font-bold text-slate-950">
                            {formatStorage(
                              pack.storageGB,
                            )}
                          </div>

                          <div className="mt-1 text-sm text-slate-600">
                            Additional secure
                            family storage
                          </div>
                        </div>

                        {selected && (
                          <span className="rounded-full bg-amber-700 p-1 text-white">
                            <Check className="h-4 w-4" />
                          </span>
                        )}
                      </div>

                      <div className="mt-4 text-xl font-extrabold text-slate-950">
                        {formatPrice(
                          price,
                        )}

                        <span className="ml-1 text-xs font-medium text-slate-500">
                          /
                          {cycle ===
                          'yearly'
                            ? 'year'
                            : 'month'}
                        </span>
                      </div>
                    </button>
                  );
                },
              )}
            </div>

            <div className="mt-5 rounded-2xl border border-slate-200 bg-white p-4">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="text-xs font-bold uppercase tracking-wider text-slate-500">
                    Selected add-on
                  </div>

                  <div className="font-serif text-lg font-bold text-slate-950">
                    {formatStorage(
                      selectedPack
                        .storageGB,
                    )}{' '}
                    KinsSpace
                  </div>
                </div>

                <div className="text-right">
                  <div className="text-xl font-extrabold text-slate-950">
                    {formatPrice(
                      selectedPrice,
                    )}
                  </div>

                  <div className="text-xs text-slate-500">
                    per{' '}
                    {cycle === 'yearly'
                      ? 'year'
                      : 'month'}
                  </div>
                </div>
              </div>
            </div>

            {error && (
              <div className="mt-4 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800">
                {error}
              </div>
            )}

            <Button
              type="button"
              onClick={() =>
                void beginCheckout()
              }
              disabled={
                loading ||
                checkingCatalog ||
                !catalogVerified
              }
              className="mt-5 w-full bg-amber-700 text-white hover:bg-amber-800"
            >
              {loading && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}

              {!hasPaidMembership
                ? 'Choose Membership First'
                : 'Continue to Secure Checkout'}
            </Button>

            <p className="text-center text-xs text-slate-500">
              No charge occurs until you
              confirm payment.
            </p>
          </>
        )}

        {step === 'payment' && (
          <>
            <button
              type="button"
              onClick={() => {
                setStep('plans');
                setError(null);
                setPaymentReady(false);
                setClientSecret(null);
                setCustomerId(null);
                setPurchaseAttemptId(null);
                setReceipt(null);
              }}
              className="mt-1 flex items-center gap-1 text-sm font-semibold text-amber-800"
            >
              <ArrowLeft className="h-4 w-4" />
              Change KinsSpace plan
            </button>

            <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-serif text-lg font-bold">
                    {formatStorage(
                      selectedPack
                        .storageGB,
                    )}{' '}
                    KinsSpace
                  </div>

                  <div className="text-sm text-slate-600">
                    Separate storage
                    subscription
                  </div>
                </div>

                <div className="text-right font-bold">
                  {formatPrice(
                    selectedPrice,
                  )}
                  <div className="text-xs font-normal text-slate-500">
                    /
                    {cycle === 'yearly'
                      ? 'year'
                      : 'month'}
                  </div>
                </div>
              </div>
            </div>

            <div className="relative mt-4 rounded-2xl border border-slate-200 bg-white p-4">
              {!paymentReady && !error && (
                <div className="absolute inset-0 z-10 flex items-center justify-center rounded-2xl bg-white">
                  <div className="flex items-center gap-2 text-sm font-semibold text-slate-600">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading secure payment form…
                  </div>
                </div>
              )}

              <div
                ref={paymentMountRef}
                className="min-h-[180px]"
              />
            </div>

            {error && (
              <div className="mt-4 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800">
                {error}
              </div>
            )}

            <Button
              type="button"
              onClick={() =>
                void activateSubscription()
              }
              disabled={
                loading ||
                !paymentReady
              }
              className="mt-5 w-full bg-amber-700 text-white hover:bg-amber-800 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {loading || !paymentReady ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Lock className="mr-2 h-4 w-4" />
              )}

              {paymentReady
                ? 'Activate KinsSpace'
                : 'Loading Secure Payment'}
            </Button>

            <p className="text-center text-xs text-slate-500">
              Your KinsCircle membership
              remains unchanged.
            </p>
          </>
        )}

        {step === 'success' && receipt && (
          <div className="py-6">
            <div className="text-center">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100">
                <CheckCircle2 className="h-8 w-8 text-emerald-700" />
              </div>

              <h3 className="mt-4 font-serif text-2xl font-bold">
                Thank you for choosing KinsSpace
              </h3>

              <p className="mt-2 font-medium text-amber-800">
                Your family memories now have more room to grow.
              </p>

              <p className="mt-2 text-slate-600">
                You now have{' '}
                {formatStorage(
                  selectedPack.storageGB,
                )}{' '}
                of additional family storage.
              </p>

              {receipt.recovered && (
                <p className="mt-2 text-sm font-semibold text-amber-700">
                  We safely recovered your existing KinsSpace subscription without creating another charge.
                </p>
              )}

              <p className="mt-3 text-sm text-slate-600">
                Thank you for trusting KinsCircle to help preserve your family's story.
                This storage is shared only with members of this family circle.
              </p>
            </div>

            <div className="mt-6 space-y-3 rounded-2xl border border-emerald-200 bg-emerald-50 p-4">
              <div className="flex justify-between gap-4 text-sm">
                <span className="text-slate-600">
                  Plan
                </span>
                <span className="font-semibold text-slate-950">
                  {formatStorage(
                    selectedPack.storageGB,
                  )}{' '}
                  KinsSpace
                </span>
              </div>

              <div className="flex justify-between gap-4 text-sm">
                <span className="text-slate-600">
                  Billing
                </span>
                <span className="font-semibold capitalize text-slate-950">
                  {cycle}
                </span>
              </div>

              <div className="flex justify-between gap-4 text-sm">
                <span className="text-slate-600">
                  Amount
                </span>
                <span className="font-semibold text-slate-950">
                  {formatPrice(
                    selectedPrice,
                  )}
                </span>
              </div>

              <div className="border-t border-emerald-200 pt-3">
                <div className="text-xs font-bold uppercase tracking-wider text-slate-500">
                  Subscription reference
                </div>

                <div className="mt-1 break-all font-mono text-xs text-slate-700">
                  {
                    receipt.subscriptionId
                  }
                </div>
              </div>

              <div className="flex items-center gap-2 text-sm font-semibold text-emerald-800">
                <ShieldCheck className="h-4 w-4" />
                Stripe and KinsCircle records agree
              </div>
            </div>

            <div className="mt-4 grid gap-2">
              {receipt.hostedInvoiceUrl && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() =>
                    window.open(
                      receipt.hostedInvoiceUrl!,
                      '_blank',
                      'noopener,noreferrer',
                    )
                  }
                >
                  View Stripe invoice
                </Button>
              )}

              {receipt.invoicePdfUrl && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() =>
                    window.open(
                      receipt.invoicePdfUrl!,
                      '_blank',
                      'noopener,noreferrer',
                    )
                  }
                >
                  Open invoice PDF
                </Button>
              )}

              {receipt.receiptUrl && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() =>
                    window.open(
                      receipt.receiptUrl!,
                      '_blank',
                      'noopener,noreferrer',
                    )
                  }
                >
                  View payment receipt
                </Button>
              )}
            </div>

            {!receipt.hostedInvoiceUrl &&
              !receipt.invoicePdfUrl &&
              !receipt.receiptUrl && (
                <p className="mt-4 text-center text-xs text-slate-500">
                  Stripe has not published a receipt link yet. Your
                  subscription reference is recorded above.
                </p>
              )}

            <Button
              type="button"
              onClick={() =>
                onOpenChange(false)
              }
              className="mt-6 w-full bg-amber-700 text-white hover:bg-amber-800"
            >
              Done
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default KinsSpacePlansModal;
